import { useState, useEffect, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { SimpleDialog } from "@/components/ui/simple-dialog";
import { Users, RefreshCw, X, ChevronLeft, ChevronRight } from "lucide-react";

export default function ActiveNicknamesDialog({ open, onOpenChange, onPrevious, onNext, mode }) {
  const [nicknames, setNicknames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchNicknames = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/users/active-nicknames");
      setNicknames(data);
    } catch (e) {
      setError(e?.response?.data?.detail ?? "Failed to load online users");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on open, then auto-refresh every 30s
  useEffect(() => {
    if (!open) {
      clearInterval(intervalRef.current);
      return;
    }
    fetchNicknames();
    intervalRef.current = setInterval(fetchNicknames, 5_000);
    return () => clearInterval(intervalRef.current);
  }, [open, fetchNicknames]);

  const isPrivate = mode === "private";
  const accentBorder = isPrivate ? "border-emerald-500/30" : "border-orange-500/30";
  const accentHeaderBorder = isPrivate ? "border-emerald-500/15" : "border-orange-500/15";
  const accentIconBorder = isPrivate ? "border-emerald-500/40" : "border-orange-500/40";
  const accentIconBg = isPrivate ? "bg-emerald-500/5" : "bg-orange-500/5";
  const accentIcon = isPrivate ? "text-emerald-400" : "text-orange-400";
  const accentLabel = isPrivate ? "text-emerald-400" : "text-orange-400";
  const accentNavHover = isPrivate ? "hover:border-emerald-500 hover:text-emerald-400" : "hover:border-orange-500 hover:text-orange-400";
  const accentErrorBorder = isPrivate ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-orange-500/30 bg-orange-500/10 text-orange-400";
  const accentLoading = isPrivate ? "text-emerald-400" : "text-orange-400";

  return (
    <SimpleDialog open={open} onOpenChange={onOpenChange} className="max-w-lg">
      <div
        className={`bg-[#0B0E14]/95 backdrop-blur-xl border ${accentBorder} rounded-none text-white w-full max-h-[80vh] flex flex-col`}
        data-testid="active-nicknames-dialog"
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 border-b ${accentHeaderBorder} flex-shrink-0`}>
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 border ${accentIconBorder} flex items-center justify-center ${accentIconBg}`}>
              <Users className={`w-3.5 h-3.5 ${accentIcon}`} />
            </div>
            <div>
              <div className={`text-[10px] tracking-[0.3em] uppercase ${accentLabel} mb-0.5`}>
                Online Now
              </div>
              <div className="font-display font-bold text-base tracking-tight text-slate-200">
                Who's Online
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={fetchNicknames}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh online users"
              className="flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-700 text-[11px] tracking-widest uppercase text-slate-400 hover:border-slate-500 hover:text-slate-200 transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            {onPrevious && (
              <button
                onClick={onPrevious}
                aria-label="Previous panel"
                title="Settings"
                className={`flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 ${accentNavHover} transition-colors`}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            {onNext && (
              <button
                onClick={onNext}
                aria-label="Next panel"
                title="Settings"
                className={`flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 ${accentNavHover} transition-colors`}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Close"
              className={`flex items-center justify-center w-7 h-7 border border-slate-700 text-slate-400 ${accentNavHover} transition-colors`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {error && (
            <div className={`px-4 py-3 border ${accentErrorBorder} text-xs tracking-wide mb-4`}>
              {error}
            </div>
          )}

          {loading && nicknames.length === 0 ? (
            <div
              className={`${accentLoading} text-xs tracking-widest uppercase animate-pulse py-8 text-center`}
              data-testid="active-nicknames-loading"
            >
              Loading…
            </div>
          ) : nicknames.length === 0 ? (
            <div className="border border-dashed border-slate-700 p-10 text-center text-slate-500 text-sm">
              No users currently online.
            </div>
          ) : (
            <div className="space-y-1" data-testid="active-nicknames-list">
              {nicknames.map((entry) => (
                <div
                  key={entry.email}
                  className="flex items-center gap-3 px-3 py-2.5 border border-slate-800/60 hover:bg-[#121822]/60 transition-colors"
                  data-testid="active-nickname-row"
                >
                  <div className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
                  <span className="text-slate-200 text-sm font-medium">{entry.nickname}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-slate-800/60 flex-shrink-0">
          <div className="text-[10px] text-slate-600 tracking-wide">
            {nicknames.length} user{nicknames.length !== 1 ? "s" : ""} online — auto-refreshes every 5s
          </div>
        </div>
      </div>
    </SimpleDialog>
  );
}
